import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import { Form, PrimaryButton, FieldTextInput, CustomExtendedDataField, FieldLocationAutocompleteInput } from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';
import UserFieldImage from '../UserFieldImage';

import css from './SignupForm.module.css';
import IconEyeOpen from '../../../components/IconEyeOpen/IconEyeOpen';
import IconEyeClose from '../../../components/IconEyeClose.js/IconEyeClose';

const { VALID } = validators;

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const isPasswordUsedMoreThanOnce = formValues => {
  const pw = formValues.password;
  const hasPasswordString = pw != null && pw.length >= validators.PASSWORD_MIN_LENGTH;

  if (hasPasswordString) {
    // Check if password is repeated in OTHER fields (excluding password field itself)
    const otherFieldsValues = Object.entries(formValues)
      .filter(([key, value]) => key !== 'password' && key !== 'confirmPassword')
      .map(([key, value]) => value);
    
    const isPasswordRepeated = otherFieldsValues.filter(v => v === pw).length > 0;
    return isPasswordRepeated;
  }
  return false;
};

const SignupFormComponent = props => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      initialValues={{ userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes) }}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          invalid,
          intl,
          termsAndConditions,
          preselectedUserType,
          userTypes,
          userFields,
          values,
        } = formRenderProps;

      const { userType } = values || {};

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'SignupForm.emailInvalid',
        })
      );

      // password
      const passwordRequiredMessage = intl.formatMessage({
        id: 'SignupForm.passwordRequired',
      });
      const passwordMinLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooShort',
        },
        {
          minLength: validators.PASSWORD_MIN_LENGTH,
        }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooLong',
        },
        {
          maxLength: validators.PASSWORD_MAX_LENGTH,
        }
      );
      const passwordMinLength = validators.minLength(
        passwordMinLengthMessage,
        validators.PASSWORD_MIN_LENGTH
      );
      const passwordMaxLength = validators.maxLength(
        passwordMaxLengthMessage,
        validators.PASSWORD_MAX_LENGTH
      );
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
      
      // Password validators - only validate password itself, not match with confirm
      const passwordValidators = validators.composeValidators(
        passwordRequired,
        passwordMinLength,
        passwordMaxLength
      );

      // confirm password
      const confirmPasswordRequiredMessage = intl.formatMessage({
        id: 'SignupForm.confirmPasswordRequired',
      });
      const confirmPasswordMismatchMessage = intl.formatMessage({
        id: 'SignupForm.confirmPasswordMismatch',
      });
      const confirmPasswordRequired = validators.requiredStringNoTrim(confirmPasswordRequiredMessage);
      
      // This validation function checks if confirm password matches password
      const confirmPasswordMatches = (value, allValues) => {
        console.log('Confirm password validation:', { 
          value, 
          password: allValues?.password, 
          areEqual: value === allValues?.password 
        });
        
        // If confirm password is empty, show required error if password is filled
        if (!value) {
          if (allValues?.password) {
            return confirmPasswordRequiredMessage;
          }
          return VALID; // Both empty, no error yet
        }
        
        // Always check if passwords match when confirm password has value
        if (value !== allValues?.password) {
          return confirmPasswordMismatchMessage;
        }
        
        return VALID;
      };
      
      const confirmPasswordValidators = validators.composeValidators(
        confirmPasswordRequired,
        confirmPasswordMatches
      );

      // Custom user fields are filtered based on the selected user type.
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

      const noUserTypes = !userType && !(userTypes?.length > 0);
      const userTypeConfig = userTypes.find(config => config.userType === userType);
      const showDefaultUserFields = true;
      const showCustomUserFields = !!userType && userFieldProps?.length > 0;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      
      // Check if passwords match for submit button
      const passwordsMatch = values?.password === values?.confirmPassword;
      const bothPasswordsFilled = values?.password && values?.confirmPassword;
      const passwordValid = bothPasswordsFilled ? passwordsMatch : false;
      
      // Form is valid if:
      // 1. No validation errors (invalid is false from react-final-form) AND
      // 2. Either both passwords are empty (not required yet) OR they match when filled
      const formValid = !invalid && (bothPasswordsFilled ? passwordsMatch : true);
      
      const submitDisabled = submitInProgress || !formValid || isPasswordUsedMoreThanOnce(values);
      
      console.log('Form state:', {
        invalid,
        submitInProgress,
        passwordsMatch,
        bothPasswordsFilled,
        passwordValid,
        formValid,
        isPasswordUsedMoreThanOnce: isPasswordUsedMoreThanOnce(values),
        submitDisabled,
        values: {
          password: values?.password,
          confirmPassword: values?.confirmPassword,
          allValues: values
        }
      });

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FieldSelectUserType
            name="userType"
            userTypes={userTypes}
            hasExistingUserType={!!preselectedUserType}
            intl={intl}
          />

          {showDefaultUserFields ? (
            <div className={css.defaultUserFields}>
              <FieldTextInput
                type="email"
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                autoComplete="email"
                label={intl.formatMessage({
                  id: 'SignupForm.emailLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.emailPlaceholder',
                })}
                validate={validators.composeValidators(emailRequired, emailValid)}
              />
              <div className={css.name}>
                <FieldTextInput
                  className={css.firstNameRoot}
                  type="text"
                  id={formId ? `${formId}.fname` : 'fname'}
                  name="fname"
                  autoComplete="given-name"
                  label={intl.formatMessage({
                    id: userType === 'collaborator' ? 'SignupForm.fullNameOrCompanyNameLabel' : 'SignupForm.firstNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: userType === 'collaborator' ? 'SignupForm.fullNameOrCompanyNamePlaceholder' : 'SignupForm.firstNamePlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: userType === 'collaborator' ? 'SignupForm.fullNameOrCompanyNameRequired' : 'SignupForm.firstNameRequired',
                    })
                  )}
                />
                {userType !== 'collaborator' ? (
                  <FieldTextInput
                    className={css.lastNameRoot}
                    type="text"
                    id={formId ? `${formId}.lname` : 'lname'}
                    name="lname"
                    autoComplete="family-name"
                    label={intl.formatMessage({
                      id: 'SignupForm.lastNameLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.lastNamePlaceholder',
                    })}
                    validate={validators.required(
                      intl.formatMessage({
                        id: 'SignupForm.lastNameRequired',
                      })
                    )}
                  />
                ) : null}
              </div>

              <UserFieldDisplayName
                formName="SignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />

              <div style={{ position: 'relative', margin: '24px 0' }}>
              <FieldTextInput
                className={css.password}
                type={showPassword ? 'text' : 'password'}
                id={formId ? `${formId}.password` : 'password'}
                name="password"
                autoComplete="new-password"
                label={intl.formatMessage({
                  id: 'SignupForm.passwordLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.passwordPlaceholder',
                })}
                validate={passwordValidators}
              />

              <button
                type="button"
                className={css.eyeButton}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPassword ? <IconEyeOpen/> : <IconEyeClose/>}
              </button>
              </div>

              <div style={{ position: 'relative', margin: '24px 0' }}>
              <FieldTextInput
                className={css.password}
                type={showConfirmPassword ? 'text' : 'password'}
                id={formId ? `${formId}.confirmPassword` : 'confirmPassword'}
                name="confirmPassword"
                autoComplete="new-password"
                label={intl.formatMessage({
                  id: 'SignupForm.confirmPasswordLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.confirmPasswordPlaceholder',
                })}
                validate={confirmPasswordValidators}
              />

              <button
                type="button"
                className={css.eyeButton}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showConfirmPassword ? <IconEyeOpen/> : <IconEyeClose/>}
              </button>
              </div>

              <UserFieldPhoneNumber
                formName="SignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />
            </div>
          ) : null}

          {showCustomUserFields ? (
            <div className={css.customFields}>
              {userFieldProps.map(({ key, ...fieldProps }) => {
                const { fieldConfig } = fieldProps;
                if (fieldConfig.key === 'logo') {
                  return (
                    <UserFieldImage
                      key={key}
                      formId={formId}
                      fieldConfig={fieldConfig}
                      intl={intl}
                    />
                  );
                }
                return <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />;
              })}
            </div>
          ) : null}

          <div className={css.bottomWrapper}>
            {termsAndConditions}
            {isPasswordUsedMoreThanOnce(values) ? (
              <div className={css.error}>
                <FormattedMessage id="SignupForm.passwordRepeatedOnOtherFields" />
              </div>
            ) : null}
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="SignupForm.signUp" />
            </PrimaryButton>
          </div>
        </Form>
      );
      }}
    />
  );
};

/**
 * A component that renders the signup form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {ReactNode} props.termsAndConditions - The terms and conditions
 * @param {string} props.preselectedUserType - The preselected user type
 * @param {propTypes.userTypes} props.userTypes - The user types
 * @param {propTypes.listingFields} props.userFields - The user fields
 * @returns {JSX.Element}
 */
const SignupForm = props => {
  const intl = useIntl();
  return <SignupFormComponent {...props} intl={intl} />;
};

export default SignupForm;